import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

// 영어 문장 데이터 타입 정의
interface EnglishSentence {
  id: number;
  text: string;
  translation: string;
  lang: string;
}

const Dashboard: React.FC = () => {
  const { user, hasPermission } = useAuth();

  // 오늘의 영어 문장 상태
  const [todaySentence, setTodaySentence] = useState<EnglishSentence | null>(null);
  const [sentenceLoading, setSentenceLoading] = useState(false);
  const [sentenceError, setSentenceError] = useState<string | null>(null);

  // 시스템 정보 모달 상태
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [systemHistory, setSystemHistory] = useState<any[]>([]);
  const [newSystemInfo, setNewSystemInfo] = useState({
    title: '',
    version: '',
    description: ''
  });

  const dashboardCards = [
    {
      title: '서비스 리포트',
      description: '현장 서비스 작업 내용을 관리합니다',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M9 7h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3"/>
          <path d="M9 15h3l8.5 -8.5a1.5 1.5 0 0 0 -3 -3l-8.5 8.5v3"/>
          <line x1="16" y1="5" x2="19" y2="8"/>
        </svg>
      ),
      link: '/service-reports',
      permission: 'service_report',
      color: 'bg-primary'
    },
    {
      title: '거래명세서',
      description: '서비스 비용 및 거래 내역을 관리합니다',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M17 8v-3a1 1 0 0 0 -1 -1h-10a2 2 0 0 0 0 4h12a1 1 0 0 1 1 1v3m0 4v3a1 1 0 0 1 -1 1h-12a2 2 0 0 1 -2 -2v-12"/>
          <path d="M20 12v4h-4a2 2 0 0 1 0 -4h4"/>
        </svg>
      ),
      link: '/transactions',
      permission: 'transaction',
      color: 'bg-success'
    },
    {
      title: '고객정보',
      description: '고객사 정보 및 연락처를 관리합니다',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <circle cx="12" cy="7" r="4"/>
          <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/>
        </svg>
      ),
      link: '/customers',
      permission: 'customer',
      color: 'bg-info'
    },
    {
      title: '스페어파트',
      description: '부품 재고 및 가격 정보를 관리합니다',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z"/>
          <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
          <path d="M12 7l0 .01"/>
          <path d="M12 17l0 .01"/>
          <path d="M7 12l0 .01"/>
          <path d="M17 12l0 .01"/>
        </svg>
      ),
      link: '/spare-parts',
      permission: 'spare_parts',
      color: 'bg-warning'
    }
  ];

  // 백엔드를 통해 Tatoeba API에서 영어 문장 가져오기
  const fetchTodaysEnglishSentence = async () => {
    setSentenceLoading(true);
    setSentenceError(null);

    try {
      // 환경에 따른 API URL 설정
      const API_BASE_URL = process.env.REACT_APP_API_URL || (
        process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000'
      );

      // 백엔드 API 호출
      const response = await fetch(
        `${API_BASE_URL}/api/tatoeba/random-sentence`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch sentence');
      }

      const data = await response.json();

      if (data.success && data.sentence) {
        setTodaySentence({
          id: data.sentence.id,
          text: data.sentence.text,
          translation: data.sentence.translation,
          lang: data.sentence.lang
        });
      } else {
        // API 응답에 문장이 없으면 대체 문장 사용
        setTodaySentence({
          id: 0,
          text: "The journey of a thousand miles begins with one step.",
          translation: "천 리 길도 한 걸음부터 시작됩니다.",
          lang: "eng"
        });
      }
    } catch (error) {
      console.error('Error fetching English sentence:', error);
      setSentenceError('영어 문장을 불러오는데 실패했습니다.');

      // 오류 시 대체 문장 표시
      setTodaySentence({
        id: 0,
        text: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
        translation: "성공은 최종적인 것이 아니고, 실패는 치명적인 것이 아닙니다. 중요한 것은 계속할 수 있는 용기입니다.",
        lang: "eng"
      });
    } finally {
      setSentenceLoading(false);
    }
  };

  // 시스템 정보 이력 가져오기
  const fetchSystemHistory = async () => {
    try {
      const response = await fetch('/api/system/info-history', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSystemHistory(data.history);
      }
    } catch (error) {
      console.error('시스템 정보 조회 실패:', error);
    }
  };

  // 시스템 정보 추가
  const handleAddSystemInfo = async () => {
    if (!newSystemInfo.title || !newSystemInfo.version) {
      alert('시스템 이름과 버전은 필수입니다.');
      return;
    }

    try {
      const response = await fetch('/api/system/info-history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newSystemInfo)
      });

      const data = await response.json();
      if (data.success) {
        alert('시스템 정보가 추가되었습니다.');
        setShowAddModal(false);
        setNewSystemInfo({ title: '', version: '', description: '' });
        fetchSystemHistory();
      } else {
        alert(data.message || '시스템 정보 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('시스템 정보 추가 실패:', error);
      alert('시스템 정보 추가에 실패했습니다.');
    }
  };

  // 컴포넌트 마운트 시 영어 문장 및 시스템 정보 가져오기
  useEffect(() => {
    fetchTodaysEnglishSentence();
    fetchSystemHistory();
  }, []);

  const availableCards = dashboardCards.filter(card => hasPermission(card.permission));

  return (
    <div className="page-header d-print-none">
      <div className="container-xl">
        <div className="row g-2 align-items-center">
          <div className="col">
            <h2 className="page-title">
              대시보드
            </h2>
            <div className="text-muted mt-1">
              안녕하세요, {user?.name}님!
            </div>
          </div>
        </div>
      </div>
      
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-deck row-cards">
            {/* 첫 번째 줄: 사용자 정보, 접근 권한, 시스템 정보 */}
            {/* 사용자 정보 카드 */}
            <div className="col-12 col-md-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">사용자 정보</div>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <div className="h1 mb-0 me-2">{user?.is_admin ? 'admin' : user?.name}</div>
                  </div>
                  <div className="text-muted">
                    <strong>부서:</strong> {user?.department}<br />
                    <strong>이메일:</strong> {user?.email}<br />
                    <strong>연락처:</strong> {user?.contact || '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* 권한 정보 카드 */}
            <div className="col-12 col-md-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">접근 권한</div>
                  </div>
                  <div className="mt-2">
                    {user?.is_admin ? (
                      <span
                        className="badge me-1"
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #e53e3e',
                          color: '#e53e3e'
                        }}
                      >
                        관리자
                      </span>
                    ) : (
                      <div className="d-flex gap-1 flex-wrap">
                        <span
                          className="badge"
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #4299e1',
                            color: '#4299e1',
                            filter: user?.service_report_access ? 'none' : 'blur(1.5px)',
                            opacity: user?.service_report_access ? 1 : 0.5
                          }}
                        >
                          리포트
                        </span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #dd6b20',
                            color: '#dd6b20',
                            filter: user?.customer_access ? 'none' : 'blur(1.5px)',
                            opacity: user?.customer_access ? 1 : 0.5
                          }}
                        >
                          고객
                        </span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #d53f8c',
                            color: '#d53f8c',
                            filter: user?.resource_access ? 'none' : 'blur(1.5px)',
                            opacity: user?.resource_access ? 1 : 0.5
                          }}
                        >
                          리소스
                        </span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #38a169',
                            color: '#38a169',
                            filter: user?.transaction_access ? 'none' : 'blur(1.5px)',
                            opacity: user?.transaction_access ? 1 : 0.5
                          }}
                        >
                          거래
                        </span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #805ad5',
                            color: '#805ad5',
                            filter: user?.spare_parts_access ? 'none' : 'blur(1.5px)',
                            opacity: user?.spare_parts_access ? 1 : 0.5
                          }}
                        >
                          부품
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 시스템 정보 카드 */}
            <div className="col-12 col-md-4">
              <div
                className="card"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowSystemModal(true)}
              >
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">시스템 정보</div>
                  </div>
                  <div className="h1 mb-0 me-2">
                    {systemHistory.length > 0 ? systemHistory[0].title : 'LVDK Webtranet'}
                  </div>
                  <div className="text-muted">
                      <span
                        className="badge me-1"
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #347bffff',
                          color: '#347bffff'
                        }}
                      >
                        {systemHistory.length > 0 ? systemHistory[0].version : 'v1.0 BETA'}
                      </span><br />
                    {systemHistory.length > 0 ? (
                      systemHistory[0].description?.split('\n').map((line: string, idx: number) => (
                        <React.Fragment key={idx}>
                          {line}<br />
                        </React.Fragment>
                      ))
                    ) : (
                      <>

                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 두 번째 줄: 오늘의 영어 한문장 (전체 너비) */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="subheader d-flex align-items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="m0 0h24v24H0z" fill="none"/>
                        <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/>
                        <path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/>
                        <line x1="3" y1="6" x2="3" y2="19"/>
                        <line x1="12" y1="6" x2="12" y2="19"/>
                        <line x1="21" y1="6" x2="21" y2="19"/>
                      </svg>
                      오늘의 영어 한문장
                    </div>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={fetchTodaysEnglishSentence}
                      disabled={sentenceLoading}
                      title="새 문장 가져오기"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="m0 0h24v24H0z" fill="none"/>
                        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/>
                        <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/>
                      </svg>
                    </button>
                  </div>

                  {sentenceLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : sentenceError ? (
                    <div className="alert alert-warning mb-0">
                      <small>{sentenceError}</small>
                    </div>
                  ) : todaySentence ? (
                    <div>
                      <blockquote className="blockquote mb-2">
                        <p className="mb-2" style={{ fontSize: '1rem', lineHeight: '1.5', fontWeight: '500' }}>
                          "{todaySentence.text}"
                        </p>
                      </blockquote>
                      <div className="mb-3 ps-3" style={{ borderLeft: '3px solid #0d6efd' }}>
                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                          {todaySentence.translation}
                        </p>
                      </div>
                      <div className="text-muted small d-flex align-items-center justify-content-between">
                        <span>
                          출처: <a href="https://tatoeba.org" target="_blank" rel="noopener noreferrer" className="text-decoration-none">Tatoeba.org</a>
                        </span>
                        {todaySentence.id > 0 && (
                          <span>ID: {todaySentence.id}</span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* 월별 재고현황 바로가기 - 스페어파트 권한이 있는 사용자에게만 표시 */}
            {hasPermission('spare_parts') && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card dashboard-card" style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/monthly-inventory'}>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="subheader mb-2">월별 재고현황</div>
                    </div>
                    <div className="d-flex align-items-baseline">
                      <div className="h1 mb-0 me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-table" width="48" height="48" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <rect x="4" y="4" width="16" height="16" rx="2"/>
                          <line x1="4" y1="10" x2="20" y2="10"/>
                          <line x1="10" y1="4" x2="10" y2="20"/>
                        </svg>
                      </div>
                      <div>
                        <div className="text-muted">파트별 월별 입출고 현황</div>
                        <div className="text-muted small">연도별 집계 및 엑셀 다운로드</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* YTD 요약 바로가기 - 모든 사용자에게 표시 */}
            <div className="col-12 col-md-6 col-lg-3">
              <div className="card dashboard-card" style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/ytd-summary'}>
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader mb-2">YTD 요약</div>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <div className="h1 mb-0 me-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chart-bar" width="48" height="48" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <rect x="3" y="12" width="6" height="8" rx="1"/>
                        <rect x="9" y="8" width="6" height="12" rx="1"/>
                        <rect x="15" y="4" width="6" height="16" rx="1"/>
                        <line x1="4" y1="20" x2="18" y2="20"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-muted">Invoice Code별 월별/분기별</div>
                      <div className="text-muted small">Work/Travel/Parts 비용 집계</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 세 번째 줄부터: 메뉴 카드들 */}
            {availableCards.map((card, index) => (
              <div key={index} className="col-12 col-md-6 col-lg-3">
                <div className="card dashboard-card">
                  <div className="card-body">
                    <div className={`avatar avatar-lg mb-3 ${card.color} text-white`}>
                      {card.icon}
                    </div>
                    <h3 className="card-title">{card.title}</h3>
                    <p className="text-muted">{card.description}</p>
                    <a href={card.link} className="btn btn-outline-primary">
                      바로가기
                    </a>
                  </div>
                </div>
              </div>
            ))}

            {user?.is_admin && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card dashboard-card">
                  <div className="card-body">
                    <div className="avatar avatar-lg mb-3 bg-danger text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"/>
                      </svg>
                    </div>
                    <h3 className="card-title">사용자 관리</h3>
                    <p className="text-muted">직원 계정 및 권한을 관리합니다</p>
                    <a href="/user-management" className="btn btn-outline-primary">
                      바로가기
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 시스템 정보 이력 모달 */}
      {showSystemModal && (
        <div
          className="modal modal-blur show"
          style={{ display: 'block' }}
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">시스템 정보 이력</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSystemModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {systemHistory.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    등록된 시스템 정보가 없습니다.
                  </div>
                ) : (
                  <div className="timeline">
                    {systemHistory.map((item, index) => (
                      <div key={item.id} className="timeline-item">
                        <div className="timeline-badge bg-primary"></div>
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h3 className="mb-1">{item.title}</h3>
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #347bffff',
                                    color: '#347bffff'
                                  }}
                                >
                                  {item.version}
                                </span>
                              </div>
                              {index === 0 && (
                                <span className="badge bg-success">최신</span>
                              )}
                            </div>
                            <div className="text-muted mb-2" style={{ whiteSpace: 'pre-line' }}>
                              {item.description}
                            </div>
                            <div className="text-muted small">
                              <strong>등록자:</strong> {item.created_by_name || '시스템'} |
                              <strong> 등록일:</strong> {new Date(item.created_at).toLocaleString('ko-KR')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {user?.is_admin && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowSystemModal(false);
                      setShowAddModal(true);
                    }}
                  >
                    새 정보 추가
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSystemModal(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 시스템 정보 추가 모달 */}
      {showAddModal && (
        <div
          className="modal modal-blur show"
          style={{ display: 'block' }}
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">새 시스템 정보 추가</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewSystemInfo({ title: '', version: '', description: '' });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">시스템 이름 *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newSystemInfo.title}
                    onChange={(e) => setNewSystemInfo({ ...newSystemInfo, title: e.target.value })}
                    placeholder="예: LVDK Webtranet"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">버전 *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newSystemInfo.version}
                    onChange={(e) => setNewSystemInfo({ ...newSystemInfo, version: e.target.value })}
                    placeholder="예: v1.0 BETA"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">설명</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={newSystemInfo.description}
                    onChange={(e) => setNewSystemInfo({ ...newSystemInfo, description: e.target.value })}
                    placeholder="변경 사항이나 새로운 기능에 대한 설명을 입력하세요"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewSystemInfo({ title: '', version: '', description: '' });
                  }}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddSystemInfo}
                >
                  추가
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;